import React, { Suspense } from 'react';
import { motion } from 'framer-motion';

// Loading component
const LoadingSpinner = () => (
  <div className="loading-spinner">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

// Intersection Observer Hook for lazy loading
export const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = React.useState(false);
  const [hasIntersected, setHasIntersected] = React.useState(false);
  const ref = React.useRef();

  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasIntersected) {
        setIsIntersecting(true);
        setHasIntersected(true);
      }
    }, {
      threshold: 0.1,
      rootMargin: '50px',
      ...options
    });

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasIntersected, options]);

  return [ref, isIntersecting];
};

// Lazy Section Component
export const LazySection = ({ children, fallback = <LoadingSpinner />, ...props }) => (
  <Suspense fallback={fallback}>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6 }}
      {...props}
    >
      {children}
    </motion.div>
  </Suspense>
);

// Optimized Image Component
export const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  loading = 'lazy',
  ...props 
}) => {
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);

  const handleLoad = React.useCallback(() => setImageLoaded(true), []);
  const handleError = React.useCallback(() => setImageError(true), []);

  return (
    <div className={`image-container ${className}`} {...props}>
      {!imageLoaded && !imageError && (
        <div className="image-placeholder">
          <div className="placeholder-content">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      )}
      
      {imageError ? (
        <div className="image-error">
          <i className="fas fa-image text-muted"></i>
          <span className="text-muted">Image not available</span>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          loading={loading}
          onLoad={handleLoad}
          onError={handleError}
          className={`optimized-image ${imageLoaded ? 'loaded' : 'loading'}`}
          style={{ opacity: imageLoaded ? 1 : 0 }}
        />
      )}
    </div>
  );
};

// Preload Component for critical resources
export const PreloadResources = () => {
  React.useEffect(() => {
    // Preload critical images
    const preloadImage = (src) => {
      const img = new Image();
      img.src = src;
    };

    // Preload critical images
    preloadImage('/images/hero-image.png');
    preloadImage('/images/FINAL-LOGO-GV.png');

    // DNS prefetch for external resources
    const prefetchLink = (href) => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = href;
      document.head.appendChild(link);
    };

    prefetchLink('//fonts.googleapis.com');
    prefetchLink('//fonts.gstatic.com');
    prefetchLink('//images.unsplash.com');
  }, []);

  return null;
};

// Performance monitoring hook
export const usePerformanceMonitor = () => {
  React.useEffect(() => {
    // Monitor Core Web Vitals
    if ('web-vital' in window) {
      import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
        getCLS(console.log);
        getFID(console.log);
        getFCP(console.log);
        getLCP(console.log);
        getTTFB(console.log);
      });
    }

    // Monitor page load time
    window.addEventListener('load', () => {
      const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
      console.log(`Page load time: ${loadTime}ms`);
    });
  }, []);
};

// Default export for the main component
const PerformanceOptimizer = () => null;

export default PerformanceOptimizer;
